/**
 * Factory helpers for common React Query mutation patterns.
 *
 * These cover the three update shapes that appear across every integration
 * hook file (Sentry, Linear, PR-Review, …):
 *
 *   • makeDeleteMutation  – removes an item from a cached list by id
 *   • makeToggleMutation  – replaces a single item after enable/disable
 *   • makeUpdateMutation  – replaces a single item after an update
 *   • makeCreateMutation  – appends a new item to a cached list
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// makeDeleteMutation
// ---------------------------------------------------------------------------

export function makeDeleteMutation<T extends { id: string }>(
  queryKey: readonly unknown[],
  mutationFn: (id: string) => Promise<unknown>,
) {
  return function useDeleteMutation() {
    const qc = useQueryClient();
    return useMutation({
      mutationFn,
      onSuccess: (_data, id) => {
        qc.setQueryData<T[]>(queryKey, (old) => old?.filter((p) => p.id !== id) ?? []);
      },
    });
  };
}

// ---------------------------------------------------------------------------
// makeToggleMutation
// ---------------------------------------------------------------------------

export function makeToggleMutation<T extends { id: string }>(
  queryKey: readonly unknown[],
  mutationFn: (id: string, enabled: boolean) => Promise<T>,
) {
  return function useToggleMutation() {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => mutationFn(id, enabled),
      onSuccess: (updated) => {
        qc.setQueryData<T[]>(
          queryKey,
          (old) => old?.map((p) => (p.id === updated.id ? updated : p)) ?? [],
        );
      },
    });
  };
}

// ---------------------------------------------------------------------------
// makeUpdateMutation
// ---------------------------------------------------------------------------

export function makeUpdateMutation<T extends { id: string }, U>(
  queryKey: readonly unknown[],
  mutationFn: (id: string, data: U) => Promise<T>,
) {
  return function useUpdateMutation() {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: ({ id, data }: { id: string; data: U }) => mutationFn(id, data),
      onSuccess: (updated) => {
        qc.setQueryData<T[]>(
          queryKey,
          (old) => old?.map((p) => (p.id === updated.id ? updated : p)) ?? [],
        );
      },
    });
  };
}

// ---------------------------------------------------------------------------
// makeCreateMutation
// ---------------------------------------------------------------------------

export function makeCreateMutation<T, C>(
  queryKey: readonly unknown[],
  mutationFn: (data: C) => Promise<T>,
) {
  return function useCreateMutation() {
    const qc = useQueryClient();
    return useMutation({
      mutationFn,
      onSuccess: (created) => {
        qc.setQueryData<T[]>(queryKey, (old) => (old ? [...old, created] : [created]));
      },
    });
  };
}

// ---------------------------------------------------------------------------
// makeInvalidatingMutation
// ---------------------------------------------------------------------------

/**
 * Generic mutation that invalidates a query key on success.
 * Use when the mutation response shouldn't be merged optimistically
 * (e.g. the server may add/transform fields, or the entity goes through
 * async status transitions).
 */
export function makeInvalidatingMutation<T, C>(
  queryKey: readonly unknown[],
  mutationFn: (data: C) => Promise<T>,
) {
  return function useInvalidatingMutation() {
    const qc = useQueryClient();
    return useMutation({
      mutationFn,
      onSuccess: () => {
        qc.invalidateQueries({ queryKey });
      },
    });
  };
}
